import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { download, post } from "../lib/api";
import { plural } from "../lib/format";
import { Button, Card, Input, Modal, Spinner } from "../ui";
import { useShell } from "../shell/context";

// Data-subject actions on a contact (E9): export everything Horus holds
// about them, or erase them for good.
export function usePrivacyActions(contact) {
    const { toast, bump } = useShell();
    const history = useHistory();
    const [eraseOpen, setEraseOpen] = useState(false);
    const [exporting, setExporting] = useState(false);

    const exportData = async () => {
        setExporting(true);
        try {
            const name = await download(`v1/crm/contacts/${contact.id}/export`, { format: "zip" }, `horus-contact-${contact.id}.zip`);
            toast(`Downloaded ${name}.`, { icon: "ri-download-2-line" });
        } catch (e) {
            toast(e.message, { error: true });
        } finally {
            setExporting(false);
        }
    };

    const onErased = (res) => {
        const c = (res && res.counts) || {};
        const extra = c.contacts > 1 ? ` with ${plural(c.contacts - 1, "merged duplicate")}` : "";
        toast(`${contact.name} was erased${extra}.`, { icon: "ri-shield-check-line" });
        bump();
        history.push("/crm/contacts");
    };

    const modal = <EraseModal open={eraseOpen} contact={contact} onClose={() => setEraseOpen(false)} onErased={onErased} />;
    return { exportData, exporting, openErase: () => setEraseOpen(true), modal };
}

function EraseModal({ open, contact, onClose, onErased }) {
    const [typed, setTyped] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const matches = typed.trim().toLocaleLowerCase("tr-TR") === String(contact.name || "").trim().toLocaleLowerCase("tr-TR");

    const close = () => {
        if (busy) return;
        setTyped("");
        setError(null);
        onClose();
    };

    const erase = async () => {
        if (!matches) return;
        setBusy(true);
        setError(null);
        try {
            const res = await post(`v1/crm/contacts/${contact.id}/erase`, { confirm: true });
            setBusy(false);
            setTyped("");
            onClose();
            onErased(res);
        } catch (e) {
            setError(e.message);
            setBusy(false);
        }
    };

    return (
        <Modal
            open={open}
            title="Erase person"
            onClose={close}
            width={520}
            footer={
                <>
                    <Button variant="light" onClick={close} disabled={busy}>Cancel</Button>
                    <Button variant="danger" disabled={!matches || busy} onClick={erase}>
                        {busy && <Spinner />}
                        Erase permanently
                    </Button>
                </>
            }
        >
            <div className="hz-form-grid">
                {error && <div className="hz-alert hz-alert-danger">{error}</div>}
                <div className="hz-alert hz-alert-danger">
                    <i className="ri-error-warning-line"></i> This is permanent and cannot be undone. Unlike Delete, nothing goes to the trash.
                </div>
                <div>
                    <div className="hz-strong" style={{ marginBottom: 4 }}>What happens to {contact.name}</div>
                    <ul className="hz-bullets">
                        <li>The person, their roles, tags, tier history and touches with only them are deleted, with any duplicates merged into them.</li>
                        <li>Touches that also name a company and leads stay, without the person; a note on each lead says a contact was erased.</li>
                        <li>Their name, email, LinkedIn URL and phone are replaced with [erased] in other notes, touches, leads, signals, history and stored import files.</li>
                        <li>History keeps a record that an erase happened, without personal data.</li>
                        <li>Database backups still hold older copies until they expire (30 days).</li>
                    </ul>
                    <div className="hz-small hz-muted">Export their data first if the request asks for a copy.</div>
                </div>
                <label className="hz-field">
                    <span className="hz-label">Type <strong>{contact.name}</strong> to confirm</span>
                    <Input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" onKeyDown={(e) => e.key === "Enter" && erase()} />
                </label>
            </div>
        </Modal>
    );
}

export default function PrivacyCard({ actions }) {
    return (
        <Card title="Privacy">
            <div className="hz-small hz-muted" style={{ marginBottom: 10 }}>For a data-subject request (KVKK/GDPR): a copy of everything held, or erasure.</div>
            <div className="hz-row-wrap">
                <Button variant="outline" icon="ri-download-2-line" disabled={actions.exporting} onClick={actions.exportData}>
                    {actions.exporting && <Spinner />}Export data
                </Button>
                <Button variant="outline" icon="ri-eraser-line" className="hz-btn-danger-outline" onClick={actions.openErase}>Erase person…</Button>
            </div>
        </Card>
    );
}
